import { useEffect } from 'react';
import { useFormContext, UseFormRegisterReturn } from 'react-hook-form';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';

import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Label from './Label';

import '@mantine/core/styles/List.css';
import '@mantine/core/styles.css';

export interface RichTextEditorProps {
    name: string;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    register?: UseFormRegisterReturn;
    required?: boolean;
}

export default function CustomRichTextEditor({
    name,
    label,
    placeholder,
    disabled = false,
    className,
    register,
    required
}: RichTextEditorProps) {
    const { setValue, getValues } = useFormContext();

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link,
            Highlight,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder: placeholder ?? 'Edit here' })
        ],
        content: getValues(name) ?? '',
        editable: !disabled,
        onUpdate({ editor }) {
            setValue(name, editor.getHTML(), {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });
        },
    });

    useEffect(() => {
        if (editor && getValues(name) !== editor.getHTML()) {
            editor.commands.setContent(getValues(name) ?? '');
        }
    }, [getValues(name), editor]);

    return (
        <div className={`flex flex-col text-left z-10 ${className} text-sm`}>
            {label && (
                <Label 
                    name={name} 
                    text={label} 
                    required={required}
                    className={""}
                    hint={""}
                />
            )}
            <div className='w-full'>
                <RichTextEditor editor={editor} className="text-sm">
                    <RichTextEditor.Toolbar>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Bold />
                            <RichTextEditor.Italic />
                            <RichTextEditor.Underline />
                            <RichTextEditor.Strikethrough />
                            <RichTextEditor.ClearFormatting />
                            <RichTextEditor.Highlight />
                            <RichTextEditor.Code />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.H1 />
                            <RichTextEditor.H2 />
                            <RichTextEditor.H3 />
                            <RichTextEditor.H4 />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Blockquote />
                            <RichTextEditor.Hr />
                            <RichTextEditor.BulletList />
                            <RichTextEditor.OrderedList />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Link />
                            <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.AlignLeft />
                            <RichTextEditor.AlignCenter />
                            <RichTextEditor.AlignJustify />
                            <RichTextEditor.AlignRight />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Undo />
                            <RichTextEditor.Redo />
                        </RichTextEditor.ControlsGroup>
                    </RichTextEditor.Toolbar>
                    <div>
                        <RichTextEditor.Content className="text-sm" />
                    </div>
                </RichTextEditor>
            </div>
            {register?.ref && (
                <input
                    type="hidden"
                    {...register}
                />
            )}
        </div>
    );
}